// Shared QC presentation values from the prototype (qcStatusColors/qcTypeTheme/qcStatusHelp) — every QC surface renders from one source.

import { QcIssueCategory, QcIssueStatus } from '../../../interfaces/qcIssue.interface';

// Base accent palette for the v2 assessment/QC surfaces — one definition per color;
// composite rgba(...) tints stay inline where alpha varies per use.
export const ACCENT_BLUE = '#1976d2';
export const ACCENT_BLUE_DARK = '#1565c0';
export const SUCCESS_GREEN = '#2e7d32';
export const ERROR_RED = '#c62828';
export const WARN_ORANGE = '#e65100';
export const FIRST_PASS_PURPLE = '#5e35b1';
// Question-type marks: the Best Practice rosette and the Additional Recommendation chart.
export const BEST_PRACTICE_PINK = '#D467E4';
export const ADDITIONAL_REC_BLUE = '#3499D2';

// Card-side status colors; the header tiles use a lighter Pending gray, which QcStatusTiles owns.
export const QC_STATUS_RGB: Record<QcIssueStatus, string> = {
  first_pass: '94,53,177',
  pending_assessor_action: '66,66,66',
  ready_for_tpn_review: '25,118,210',
  approved: '46,125,50',
  needs_update: '230,81,0',
};

export const QC_CATEGORY_RGB: Record<QcIssueCategory, string> = {
  missing_evidence: '198,40,40',
  insufficient_detail: '237,108,2',
  incorrect_answer: '173,20,87',
  needs_clarification: '106,27,154',
  other: '69,90,100',
};

export const QC_STATUS_HELP: Record<QcIssueStatus, string> = {
  first_pass: 'Drafted by First Pass — accept or edit before submitting to the assessor.',
  pending_assessor_action: 'Waiting on assessor updates before this item can move forward.',
  ready_for_tpn_review: 'Submitted to TPN Admin — awaiting review or update request.',
  approved: 'TPN Admin reviewed this item. No further action needed.',
  needs_update: 'TPN Admin requested changes — assessor to update and resubmit.',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatQcDate(iso: string): string {
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export function formatQcTime(iso: string): string {
  const d = new Date(iso);
  const hours = ((d.getHours() + 11) % 12) + 1;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes} ${d.getHours() < 12 ? 'AM' : 'PM'}`;
}

export function formatQcDateTime(iso: string): string {
  return `${formatQcDate(iso)} · ${formatQcTime(iso)}`;
}

// Merged-table chip themes: one palette for SP and Assessor columns, shared by the workspace matrix and the admin snapshot.
export const CHIP_THEMES: Record<string, { label: string; rgb: string; text: string }> = {
  implemented: { label: 'Yes', rgb: '46,125,50', text: SUCCESS_GREEN },
  compensating_control: { label: 'C/C', rgb: '123,52,163', text: '#7b34a3' },
  not_implemented: { label: 'N/I', rgb: '211,47,47', text: ERROR_RED },
  not_applicable: { label: 'N/A', rgb: '97,97,97', text: '#616161' },
};
